import React, { useState, useEffect } from "react";
import { ArrowLeft, Calendar, FileText, AlertCircle, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { applyLeave, getMyLeaveBalance } from "../../services/hrms.service";

const ManagerApplyLeave = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    leaveType: "",
    fromDate: "",
    toDate: "",
    reason: "",
    contactDuringLeave: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [calculatedDays, setCalculatedDays] = useState(0);

  const [leaveTypes, setLeaveTypes] = useState([
    { id: 1, value: "Casual Leave", label: "Casual Leave", balance: 5 },
    { id: 2, value: "Sick Leave", label: "Sick Leave", balance: 3 },
    { id: 3, value: "Earned Leave", label: "Earned Leave", balance: 4 },
    { id: 4, value: "Maternity Leave", label: "Maternity Leave", balance: 90 },
    { id: 5, value: "Paternity Leave", label: "Paternity Leave", balance: 15 },
  ]);

  // Fetch leave balance on component mount
  useEffect(() => {
    const fetchLeaveBalance = async () => {
      try {
        const response = await getMyLeaveBalance();
        if (response.status === 1 && response.data) {
          // API returns an array of leave balance objects
          // Map the response to update leave types with actual balances
          const balanceMap = {};
          if (Array.isArray(response.data)) {
            response.data.forEach(item => {
              // Assuming each item has leave_type_name and balance
              const leaveTypeName = item.leave_type_name || item.code;
              balanceMap[leaveTypeName] = item.balance || item.remaining_balance || 0;
            });
          }

          const updatedLeaveTypes = [
            { id: 1, value: "Casual Leave", label: "Casual Leave", balance: balanceMap["Casual Leave"] || balanceMap["casual"] || 5 },
            { id: 2, value: "Sick Leave", label: "Sick Leave", balance: balanceMap["Sick Leave"] || balanceMap["sick"] || 3 },
            { id: 3, value: "Earned Leave", label: "Earned Leave", balance: balanceMap["Earned Leave"] || balanceMap["earned"] || 4 },
            { id: 4, value: "Maternity Leave", label: "Maternity Leave", balance: balanceMap["Maternity Leave"] || balanceMap["maternity"] || 90 },
            { id: 5, value: "Paternity Leave", label: "Paternity Leave", balance: balanceMap["Paternity Leave"] || balanceMap["paternity"] || 15 },
          ];
          setLeaveTypes(updatedLeaveTypes);
        }
      } catch (error) {
        console.error("Error fetching leave balance:", error);
        // Keep default balances if API fails
      }
    };

    fetchLeaveBalance();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }

    // Calculate days if both dates are selected
    if (name === "fromDate" || name === "toDate") {
      const from = name === "fromDate" ? value : formData.fromDate;
      const to = name === "toDate" ? value : formData.toDate;
      
      if (from && to) {
        const fromDateObj = new Date(from);
        const toDateObj = new Date(to);
        const diffTime = Math.abs(toDateObj - fromDateObj);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        setCalculatedDays(diffDays);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.leaveType) newErrors.leaveType = "Leave type is required";
    if (!formData.fromDate) newErrors.fromDate = "From date is required";
    if (!formData.toDate) newErrors.toDate = "To date is required";
    if (!formData.reason) newErrors.reason = "Reason is required";
    
    if (formData.fromDate && formData.toDate) {
      const from = new Date(formData.fromDate);
      const to = new Date(formData.toDate);
      
      if (to < from) {
        newErrors.toDate = "To date must be after from date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Call your API here
      const user = JSON.parse(localStorage.getItem("user"));
      const selectedLeaveType = leaveTypes.find(lt => lt.value === formData.leaveType);

      const leaveData = {
        leaveTypeId: selectedLeaveType.id,
        startDate: formData.fromDate,
        endDate: formData.toDate,
        reason: formData.reason,
        contactNumber: formData.contactDuringLeave,
        employeeId: user.employeeRef,
      };

      const response = await applyLeave(leaveData);

      if (response.status === 1) {
        setSuccess(true);
        setTimeout(() => {
          navigate("/hrms/manager/my-info");
        }, 2000);
      }
    } catch (error) {
      console.error("Error applying leave:", error);
      alert("Failed to apply leave. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const selectedLeaveType = leaveTypes.find(lt => lt.value === formData.leaveType); // eslint-disable-line no-unused-vars

  return (
    <div className="min-h-screen bg-gray-50">
      <div className=" mx-auto px-6 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/hrms/manager/my-info")}
          className="flex items-center text-indigo-600 hover:text-indigo-700 mb-6 font-medium"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to My Info
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Apply for Leave</h1>
          <p className="text-gray-600">Fill in the details below to submit your leave request</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
            <div>
              <p className="font-semibold text-green-800">Leave Applied Successfully!</p>
              <p className="text-sm text-green-600">Redirecting back to My Info...</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Leave Application Form */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <form onSubmit={handleSubmit}>
              {/* Leave Type */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Leave Type *
                </label>
                <select
                  name="leaveType"
                  value={formData.leaveType}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border ${
                    errors.leaveType ? "border-red-300" : "border-gray-300"
                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                >
                  <option value="">Select leave type</option>
                  {leaveTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label} ({type.balance} days available)
                    </option>
                  ))}
                </select>
                {errors.leaveType && (
                  <p className="text-red-500 text-xs mt-1">{errors.leaveType}</p>
                )}
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    From Date *
                  </label>
                  <input
                    type="date"
                    name="fromDate"
                    value={formData.fromDate}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    className={`w-full px-4 py-3 border ${
                      errors.fromDate ? "border-red-300" : "border-gray-300"
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  />
                  {errors.fromDate && (
                    <p className="text-red-500 text-xs mt-1">{errors.fromDate}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    To Date *
                  </label>
                  <input
                    type="date"
                    name="toDate"
                    value={formData.toDate}
                    onChange={handleChange}
                    min={formData.fromDate || new Date().toISOString().split('T')[0]}
                    className={`w-full px-4 py-3 border ${
                      errors.toDate ? "border-red-300" : "border-gray-300"
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  />
                  {errors.toDate && (
                    <p className="text-red-500 text-xs mt-1">{errors.toDate}</p>
                  )}
                </div>
              </div>

              {/* Calculated Days */}
              {calculatedDays > 0 && (
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-blue-600 mr-3" />
                    <div>
                      <p className="text-sm font-semibold text-blue-800">
                        Total Leave Days: {calculatedDays}
                      </p>
                      <p className="text-xs text-blue-600">
                        From {formData.fromDate} to {formData.toDate}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Contact During Leave */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Contact Number During Leave
                </label>
                <input
                  type="tel"
                  name="contactDuringLeave"
                  value={formData.contactDuringLeave}
                  onChange={handleChange}
                  placeholder="Enter contact number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Reason */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reason for Leave *
                </label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Please provide a brief reason for your leave"
                  className={`w-full px-4 py-3 border ${
                    errors.reason ? "border-red-300" : "border-gray-300"
                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                />
                {errors.reason && (
                  <p className="text-red-500 text-xs mt-1">{errors.reason}</p>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => navigate("/hrms/manager/my-info")}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || success}
                  className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Submitting..." : "Submit Leave Request"}
                </button>
              </div>
            </form>
          </div>

          {/* Sidebar - Leave Balance & Guidelines */}
          <div className="space-y-6">
            {/* Leave Balance Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-indigo-600" />
                Your Leave Balance
              </h3>
              <div className="space-y-3">
                {leaveTypes.map((type) => (
                  <div key={type.value} className="flex items-center justify-between pb-3 border-b last:border-b-0">
                    <span className="text-sm text-gray-600">{type.label}</span>
                    <span className="font-semibold text-gray-800">{type.balance} days</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Guidelines Card */}
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-6">
              <h3 className="font-bold text-amber-900 mb-4 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                Leave Guidelines
              </h3>
              <ul className="space-y-2 text-sm text-amber-800">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Leave requests should be submitted at least 2 days in advance</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Emergency leaves must be informed to your manager immediately</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Medical certificates required for sick leaves exceeding 3 days</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Unused leaves cannot be carried forward to next year</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerApplyLeave;